/**
 * Pure half of persistence (Beta-1 plan P3-19): what goes into / comes out of
 * `Hugo_UserPreferences/{uid}.dashboards[orgId][dashboardKey]`, and how a loaded value becomes
 * the layout to show. Firestore itself is in `dashboardLayoutService.js`.
 */
import { validateLayout, createLayout } from './layoutModel.js';
import { isWidgetAllowed } from './widgetRegistry.js';
import { defaultLayoutFor } from './dashboardDefaults.js';

export const LAYOUT_STORAGE_VERSION = 1;

/** Plain, Firestore-safe copy of a layout (no undefined values, props as plain objects). */
export function serializeLayout(layout) {
  return {
    version: LAYOUT_STORAGE_VERSION,
    items: (layout?.items || []).map((it) => ({
      i: it.i,
      widgetId: it.widgetId,
      x: it.x,
      y: it.y,
      w: it.w,
      h: it.h,
      props: JSON.parse(JSON.stringify(it.props || {})),
    })),
  };
}

/**
 * Turn a stored value (may be missing, malformed, from an older registry) into the layout to
 * render.
 *   - nothing stored           → the role default (`source: 'default'`)
 *   - stored, validates clean  → as stored (`source: 'saved'`)
 *   - stored with fixes/drops  → the repaired layout, `changed: true` so the caller may re-save,
 *                                `dropped` lists unknown widget ids for the toast (P3-19 test:
 *                                deleting a widget id from the registry never breaks a layout)
 *   - stored but empty AND the default is not → still the stored empty layout (the user removed
 *                                everything on purpose)
 */
export function resolveLoadedLayout(stored, { dashboardKey, role, registry, ...ctx }) {
  const fallback = () => ({
    layout: defaultLayoutFor(dashboardKey, role, registry, ctx),
    source: 'default',
    changed: false,
    dropped: [],
  });
  if (!stored || typeof stored !== 'object' || !Array.isArray(stored.items)) return fallback();
  const { layout: valid, dropped, changed } = validateLayout(createLayout(stored.items), registry);
  // P3-21: items above the viewer's access level are silently left out (no toast — a copied or
  // View-As layout may legitimately hold them); `changed` stays false so nothing is written back.
  const items = valid.items.filter((it) => isWidgetAllowed(registry.get(it.widgetId), { role, dashboard: dashboardKey, ...ctx }));
  const layout = items.length === valid.items.length ? valid : { ...valid, items };
  return { layout, source: 'saved', changed: Boolean(changed), dropped: dropped || [] };
}
